import { LogoutService } from './../_iam/logout.service';
import { Injectable, NgZone } from '@angular/core';
import {
  Subscription,
  BehaviorSubject,
  Observable,
  merge,
  fromEvent,
  timer,
} from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

/**
 * Idle time (ms) before the session is force-logged-out.
 * 15 minutes is appropriate for a portal exposing PII, policy documents,
 * stored bank accounts and payment initiation.
 */
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class TimeoutService {
  protected $timeOut: Subscription;
  protected $header: Subscription;
  protected $activity: Subscription;
  protected logout: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  $logout: Observable<boolean> = this.logout.asObservable();

  private activityEvents$: Observable<Event>;

  constructor(
    private logoutService: LogoutService,
    private ngZone: NgZone
  ) {}

  /**
   * Configures which DOM events count as "user activity" and resets the
   * idle timer whenever one of them fires.
   */
  setEvents() {
    this.activityEvents$ = merge(
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'resize'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'touchstart'),
      fromEvent(document, 'touchend'),
      fromEvent(window, 'scroll'),
      fromEvent(window, 'wheel')
    );
  }

  /**
   * Starts watching for inactivity. Every activity event resets the
   * IDLE_TIMEOUT_MS timer; if the timer ever fires, the user is logged out.
   */
  startWatching() {
    if (!this.activityEvents$) {
      this.setEvents();
    }
    this.stopWatching();

    // Run outside Angular to avoid triggering change detection on every
    // mousemove/scroll; only re-enter the zone when we actually time out.
    this.ngZone.runOutsideAngular(() => {
      this.$activity = this.activityEvents$
        .pipe(
          tap(() => this.resetTimer()),
          switchMap(() => timer(IDLE_TIMEOUT_MS))
        )
        .subscribe(() => {
          this.ngZone.run(() => this.unsubscribe());
        });

      // Kick off the initial timer immediately, in case no activity ever fires.
      this.resetTimer();
    });
  }

  private resetTimer() {
    this.$timeOut?.unsubscribe();
    this.$timeOut = timer(IDLE_TIMEOUT_MS).subscribe(() => {
      this.ngZone.run(() => this.unsubscribe());
    });
  }

  stopWatching() {
    this.$activity?.unsubscribe();
    this.$timeOut?.unsubscribe();
  }

  onLogOut(val: boolean) {
    this.logout.next(val);
  }

  unsubscribe() {
    this.stopWatching();
    this.$header?.unsubscribe();
    alert('You have been logged out due to inactivity.');
    this.logoutService.logout();
  }
}
